import {
  getConsumptionsAndEmissionsWithIdentifiers,
  type VueFormatterServiceInterfaceV1,
  type LocaleServiceV1,
  type Logger,
} from './feature-service-types';

type WltpEntry = { formattedConsumption?: unknown; formattedEmission?: unknown } | undefined;
type WltpLogCollector = (message: string) => void;

type CaeMissingEvent = {
  event: 'CAE_MISSING';
  fa: typeof __FEATURE_APP_NAME__;
  carlineId: string;
  market: string;
  page: string;
  timestamp: string;
};

const getPagePath = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
};

const hasRequiredWltpValues = (entry: WltpEntry) => {
  const { formattedConsumption, formattedEmission } = entry || {};
  return Boolean(formattedConsumption && formattedEmission);
};

const buildCaeMissingMessage = (carlineId: string, localeService: LocaleServiceV1): string => {
  const payload: CaeMissingEvent = {
    event: 'CAE_MISSING',
    fa: __FEATURE_APP_NAME__,
    carlineId,
    market: localeService.market || localeService.countryCode || '',
    page: getPagePath(),
    timestamp: new Date().toISOString(),
  };
  return JSON.stringify(payload);
};

export const logCaeMissing = (
  carlineId: string,
  localeService: LocaleServiceV1,
  logger?: Pick<Logger, 'warn' | 'error'>,
  onCaeMissingLog?: WltpLogCollector,
): void => {
  const errorMsg = buildCaeMissingMessage(carlineId, localeService);
  onCaeMissingLog?.(errorMsg);
  logger?.error(errorMsg);
};

const hasEmptyStringKey = (wltpKey: unknown): boolean => {
  if (Array.isArray(wltpKey)) {
    return wltpKey.some((value) => typeof value === 'string' && value.trim() === '');
  }
  return wltpKey === '';
};

export const isWltpComplianceSatisfied = async (
  wltpKeys: string[] | undefined,
  vueFormatterService: VueFormatterServiceInterfaceV1,
  localeService: LocaleServiceV1,
  logger?: Pick<Logger, 'warn' | 'error'>,
  onCaeMissingLog?: WltpLogCollector,
): Promise<boolean> => {
  if (hasEmptyStringKey(wltpKeys)) {
    logCaeMissing('', localeService, logger, onCaeMissingLog);
    return false;
  }

  const keys = [...new Set((wltpKeys ?? []).filter(Boolean))];
  if (!keys.length) return true;

  try {
    const result = await getConsumptionsAndEmissionsWithIdentifiers(
      keys,
      vueFormatterService,
      localeService,
    );

    keys.forEach((key) => {
      if (!hasRequiredWltpValues(result[key])) {
        logCaeMissing(key, localeService, logger, onCaeMissingLog);
      }
    });

    const isCompliant = keys.every((key) => hasRequiredWltpValues(result[key]));

    if (typeof window !== 'undefined') {
      console.log('📊 WLTP Compliance:', {
        keys,
        result,
        isCompliant,
      });
    }

    return isCompliant;
  } catch {
    keys.forEach((key) => {
      logCaeMissing(key, localeService, logger, onCaeMissingLog);
    });
    return false;
  }
};
